import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "assets/theme/base/colors";
import {useCurrencyCodes} from "../../hooks/useCurrencyCodes";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useCurrency } from 'context/CurrencyContext';

// Same fallback chain as useKanbanFilters
const getAgentLabel = (agent) =>
    agent?.fullname || agent?.username || agent?.name || '';

function QualificationModal({ 
  isOpen, 
  onClose, 
  qualificationData, 
  handleQualificationDataChange, 
  handleQualificationConfirm,
  lead,
  axiosInstance
}) {

  // Currency related hooks

  const { currencies } = useCurrencyCodes();
  const { currency: tenantCurrency } = useCurrency();
  const [agents, setAgents] = useState([]);
  const currencyInputRef = useRef(null);
  const [currencyError, setCurrencyError] = useState(false);

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [createNewAccount, setCreateNewAccount] = useState(false);
  const [createNewContact, setCreateNewContact] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newContactName, setNewContactName] = useState({
    first_name: '',
    last_name: '',
  });

  // Get the currency from the lead or default to USD
  // const dealCurrency = lead?.currency || 'USD';
  // const currencySymbol = getCurrencySymbol(dealCurrency);

  // Initialize with lead data when the modal opens
  useEffect(() => {
    if (!isOpen || !lead) return;
    setCurrencyError(false);
    setNewAccountName(lead.company_name || '');
    setNewContactName({
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
    });
    // Prefer linking an existing account; only default to "create new" when
    // the lead has a company name and we have no accounts loaded yet.
    fetchAgents();
    setCreateNewAccount(false);
    setCreateNewContact(!!(lead.first_name || lead.last_name));
    setSelectedAccount(null);
    setSelectedContact(null);
    setContacts([]);
  }, [isOpen, lead]);

  // Load all accounts when the qualification dialog opens
  useEffect(() => {
    if (!isOpen || !axiosInstance) return;
    let cancelled = false;

    const fetchAccounts = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('/accounts/');
        if (cancelled) return;
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.accounts || [];
        const normalized = list
          .map((account) => ({
            id: account.id,
            name: account.name || account.account_name || `Account #${account.id}`,
          }))
          .filter((account) => account.id != null)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setAccounts(normalized);
      } catch (error) {
        console.error('Error fetching accounts:', error);
        if (!cancelled) setAccounts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAccounts();
    return () => {
      cancelled = true;
    };
  }, [isOpen, axiosInstance]);

  // Load contacts for the selected account
  useEffect(() => {
    if (!selectedAccount?.id || !axiosInstance || createNewAccount) {
      setContacts([]);
      return;
    }
    let cancelled = false;

    const fetchContacts = async () => {
      try {
        const response = await axiosInstance.get(
          `/accounts/${selectedAccount.id}/contacts`
        );
        if (cancelled) return;
        const list = Array.isArray(response.data) ? response.data : [];
        setContacts(
          list.map((contact) => ({
            id: contact.id,
            first_name: contact.first_name || '',
            last_name: contact.last_name || '',
            account_id: contact.account_id ?? selectedAccount.id,
          }))
        );
      } catch (error) {
        console.error('Error fetching contacts for account:', error);
        if (!cancelled) setContacts([]);
      }
    };

    fetchContacts();
    return () => {
      cancelled = true;
    };
  }, [selectedAccount, axiosInstance, createNewAccount]);

  const fetchAgents = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setAgents(response.data);

      const assignedId =
          typeof lead?.assigned_to === 'object' ? lead.assigned_to?.id : lead?.assigned_to;
      const match =
          assignedId != null
              ? response.data.find((a) => String(a.id) === String(assignedId))
              : null;
      setSelectedAgent(match || null);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const filteredContacts = contacts;

  const handleAccountChange = (event, newValue) => {
    setSelectedAccount(newValue);
    setSelectedContact(null); // Reset contact when account changes
    setCreateNewAccount(false);
  };

  const handleContactChange = (event, newValue) => {
    setSelectedContact(newValue);
    setCreateNewContact(false);
  };

  // Handle Deal Value change with currency formatting
  const handleDealValueChange = (event) => {
    const value = event.target.value;
    
    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Ensure it's a valid number
    if (numericValue === '' || !isNaN(parseFloat(numericValue))) {
      handleQualificationDataChange({
        target: {
          name: 'value',
          value: numericValue
        }
      });
    }
  };

  // Pre-select the lead's currency (or the tenant default) when the modal opens
  useEffect(() => {
    if (!isOpen || !currencies?.length || qualificationData.currency) return;
    const code = lead?.currency || tenantCurrency;
    const match = currencies.find((c) => c.code === code);
    if (match) {
      handleQualificationDataChange({ target: { name: 'currency', value: match } });
    }
    // qualificationData.currency is intentionally not a dependency: if the user
    // clears the field, we must not immediately re-fill it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currencies, lead?.currency, tenantCurrency]);

  const handleCurrencyChange = (_, newValue) => {
    if (newValue) setCurrencyError(false);
    handleQualificationDataChange({
      target: { name: 'currency', value: newValue }
    });
  };

  // Add probability validation handler
  const handleProbabilityChange = (event) => {
    const value = event.target.value;
    
    // Only allow numbers between 0 and 100
    if (value === '' || (Number(value) >= 0 && Number(value) <= 100)) {
      handleQualificationDataChange({
        target: {
          name: 'probability',
          value: value
        }
      });
    }
  };

  const handleConfirm = () => {
    // Currency is required: scroll to it, focus it, show the error, and stop
    if (!qualificationData.currency) {
      setCurrencyError(true);
      const input = currencyInputRef.current;
      if (input) {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // preventScroll so the focus call doesn't fight the smooth scroll
        input.focus({ preventScroll: true });
      }
      return;
    }

    const qualificationPayload = {
      ...qualificationData,
      currency: qualificationData.currency.code,
      account: createNewAccount
          ? { name: newAccountName, is_new: true }
          : { id: selectedAccount?.id, name: selectedAccount?.name, is_new: false },
      contact: createNewContact
          ? { ...newContactName, is_new: true }
          : { id: selectedContact?.id, name: selectedContact?.name, is_new: false },
      estimated_closure_date: calculateEstimatedClosureDate(),
      assigned_to: selectedAgent?.id   // <-- optional chaining, avoids a crash if cleared
    };
    handleQualificationConfirm(qualificationPayload);
  };

  const calculateEstimatedClosureDate = () => {
    const currentDate = new Date();
    const estimatedClosureDate = new Date(currentDate);
    estimatedClosureDate.setMonth(currentDate.getMonth() + 1);
    return estimatedClosureDate;
  };

  const sectionHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '40px',   // same height in both columns, so the first fields line up
    mb: 1,
  };

  const fieldStackSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: '26px',         // roomy enough for the currency error text
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        overflow: 'hidden',
      }}>

        {/* Header */}

        <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
        >
          <MDTypography variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
            Confirm Lead Qualification
          </MDTypography>
          <IconButton
              aria-label="Close"
              onClick={onClose}
              size="small"
              sx={{ color: '#FFFFFF', mr: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable content area */}
        <Box 
          sx={{ 
            overflowY: 'auto',
            p: 4,
            '&::-webkit-scrollbar': {
              width: '0.4em'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555'
            }
          }}
        >
          <MDBox display="flex" gap={4}>
            {/* Left Column */}
            <MDBox flex={1}>
              {/* Account Section */}
              <MDBox mb={4}>
                <Box sx={sectionHeaderSx}>
                  <MDTypography variant="subtitle2">Account Information</MDTypography>
                  <FormControlLabel
                      control={
                        <Checkbox
                            checked={createNewAccount}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCreateNewAccount(checked);
                              if (checked) {
                                setSelectedAccount(null);
                                setSelectedContact(null);
                                setContacts([]);
                              }
                            }}
                        />
                      }
                      label="Create New Account"
                      sx={{ m: 0, mr: -1 }}
                  />
                </Box>

                {createNewAccount ? (
                    <TextField
                        fullWidth
                        label="New Account Name"
                        value={newAccountName}
                        onChange={(e) => setNewAccountName(e.target.value)}
                        size="medium"
                        sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                    />
                ) : (
                    <Autocomplete
                        options={accounts}
                        loading={loading}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={selectedAccount}
                        onChange={handleAccountChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Account"
                                size="medium"
                                placeholder={loading ? 'Loading accounts...' : 'Search accounts'}
                            />
                        )}
                        sx={{
                          '& .MuiInputBase-root': { height: '40px' },
                          '& .MuiAutocomplete-input': { padding: '0px !important' },
                        }}
                    />
                )}
              </MDBox>

              {/* Contact Section */}
              <MDBox>
                <Box sx={sectionHeaderSx}>
                  <MDTypography variant="subtitle2">Contact Information</MDTypography>
                  <FormControlLabel
                      control={
                        <Checkbox
                            checked={createNewContact}
                            onChange={(e) => setCreateNewContact(e.target.checked)}
                        />
                      }
                      label="Create New Contact"
                      sx={{ m: 0, mr: -1 }}
                  />
                </Box>

                {createNewContact ? (
                    <Box sx={{ ...fieldStackSx, gap: 2 }}>
                      <TextField
                          fullWidth
                          label="First Name"
                          value={newContactName.first_name}
                          onChange={(e) =>
                              setNewContactName({ ...newContactName, first_name: e.target.value })
                          }
                          size="medium"
                          sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                      />
                      <TextField
                          fullWidth
                          label="Last Name"
                          value={newContactName.last_name}
                          onChange={(e) =>
                              setNewContactName({ ...newContactName, last_name: e.target.value })
                          }
                          size="medium"
                          sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                      />
                    </Box>
                ) : (
                    <Autocomplete
                        options={filteredContacts}
                        getOptionLabel={(option) =>
                            `${option?.first_name || ''} ${option?.last_name || ''}`.trim()
                        }
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={selectedContact}
                        onChange={handleContactChange}
                        disabled={!selectedAccount || createNewAccount}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Contact"
                                size="medium"
                                placeholder={selectedAccount ? 'Search contacts' : 'Select an account first'}
                            />
                        )}
                        sx={{
                          '& .MuiInputBase-root': { height: '40px' },
                          '& .MuiAutocomplete-input': { padding: '0px !important' },
                        }}
                    />
                )}
              </MDBox>
            </MDBox>

            {/* Right Column */}
            <MDBox flex={1}>
              <Box sx={sectionHeaderSx}>
                <MDTypography variant="subtitle2">Deal Information</MDTypography>
              </Box>

              <Box sx={fieldStackSx}>
                {/* Currency */}
                <Autocomplete
                    options={currencies}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    value={qualificationData.currency ?? null}
                    onChange={handleCurrencyChange}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    renderOption={(props, option) => (
                        <li {...props}>
                          {option.name} ({option.code})
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={currencyInputRef}
                            required
                            error={currencyError}
                            helperText={currencyError ? 'Currency is required' : ''}
                            FormHelperTextProps={{
                              sx: {
                                position: 'absolute',   // out of the layout flow, so nothing shifts
                                top: '100%',
                                left: 0,
                                m: 0,
                                mt: '4px',
                                fontSize: '0.75rem',
                                lineHeight: 1,
                              },
                            }}
                            label="Currency"
                            size="medium"
                        />
                    )}
                    sx={{
                      '& .MuiInputBase-root': { height: '40px' },
                      '& .MuiAutocomplete-input': { padding: '0px !important' },
                    }}
                />

                {/* Deal Value */}
                <TextField
                    fullWidth
                    label="Deal Value"
                    name="value"
                    type="text"
                    value={qualificationData.value}
                    onChange={handleDealValueChange}
                    size="medium"
                    InputProps={{
                      startAdornment: qualificationData.currency?.symbol ? (
                          <InputAdornment position="start">
                            {qualificationData.currency.symbol}
                          </InputAdornment>
                      ) : null,
                    }}
                    sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                />

                {/* Deal Stage */}
                <FormControl fullWidth>
                  <InputLabel>Deal Stage</InputLabel>
                  <Select
                      name="stage"
                      value={qualificationData.stage}
                      label="Deal Stage"
                      onChange={handleQualificationDataChange}
                  >
                    <MenuItem value="Qualification">Qualification</MenuItem>
                    <MenuItem value="Needs Analysis">Needs Analysis</MenuItem>
                    <MenuItem value="Proposal">Proposal</MenuItem>
                    <MenuItem value="Negotiation">Negotiation</MenuItem>
                  </Select>
                </FormControl>

                {/* Assigned To */}
                <Autocomplete
                    options={agents}
                    getOptionLabel={getAgentLabel}
                    value={selectedAgent}
                    onChange={(_, newValue) => setSelectedAgent(newValue)}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    openOnFocus
                    autoHighlight
                    renderInput={(params) => (
                        <TextField {...params} name="assigned_to" label="Assigned To" size="medium" />
                    )}
                    sx={{
                      '& .MuiInputBase-root': { height: '40px' },
                      '& .MuiAutocomplete-input': { padding: '0px !important' },
                    }}
                />

                {/* Probability */}
                <TextField
                    fullWidth
                    label="Probability (%)"
                    name="probability"
                    type="number"
                    value={qualificationData.probability}
                    onChange={handleProbabilityChange}
                    size="medium"
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    sx={{
                      '& .MuiInputBase-root': { height: '40px' },
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                      },
                      '& input[type=number]': { '-moz-appearance': 'textfield' },
                    }}
                />
              </Box>
            </MDBox>
          </MDBox>

          {/* Action Buttons */}
          <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end">
            <MDButton 
              onClick={onClose} 
              variant="outlined"
              color="secondary" 
              sx={{ marginRight: 2 }}
            >
              Cancel
            </MDButton>
            <MDButton 
              onClick={handleConfirm}
              variant="gradient"
              color="info"
            >
              Confirm Qualification
            </MDButton>
          </MDBox>
        </Box>
      </Box>
    </Modal>
  );
}

export default QualificationModal;
